import React from "react";
import ConfirmPopup from "../../../../../../../../../../components/ConfirmPopup";
import { Button, Loader, Checkbox, Segment } from "semantic-ui-react";
import apis from "../../../../../../../../../../apis";
import toastr from "toastr";
import { PdfErrorsTooltip, PdfErrorsWarning } from "./components";
import { PAYMENT_STATUSES } from "../../../../../constants/applicantStatuses";

const DownloadPassportPdf = ({ applicant, api }) => {
  const [loading, setLoading] = React.useState(false);
  const [sendToCustomer, setSendToCustomer] = React.useState(false);
  const [pdfGenerationTriggered, setPdfGenerationTriggered] = React.useState(false);
  const [skipPaymentValidation, setSkipPaymentValidation] = React.useState(false);

  // Check if payment status is invalid (not Captured)
  const hasInvalidPaymentStatus = applicant?.paymentStatus !== PAYMENT_STATUSES.Captured;

  const resetModalState = () => {
    setSendToCustomer(false);
    setSkipPaymentValidation(false);
  };

  const generatePassportPdf = async (sendToCustomerValue) => {
    setLoading(true);
    try {
      await apis.applicantDocumentHandler.passportTriggerPdf(
        api,
        applicant.id,
        sendToCustomerValue,
        skipPaymentValidation,
      );
      setPdfGenerationTriggered(true);
      toastr.success(
        `PDF has been sent to the queue for generation. Please allow a few minutes for it to be generated.`,
      );
    } catch (error) {
      if (error.response && error.response.data) {
        const { message: errorMessage } = error.response.data;
        error.message = errorMessage;
      }
      toastr.error(`${error.message}`, 'Error generating PDF', {
        timeOut: 10000, // 10 seconds
        extendedTimeOut: 5000
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const CustomContent = () => (
    <Segment basic>
      <p>Are you sure you want to generate the passport PDF?</p>
      <div style={{ marginTop: "10px" }}>
        <Checkbox
          label="Send to customer"
          checked={sendToCustomer}
          onChange={(_, data) => setSendToCustomer(data.checked)}
        />
      </div>
      {hasInvalidPaymentStatus && (
        <div style={{
          marginTop: "15px",
          padding: "10px",
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "4px"
        }}>
          <p style={{ color: "#856404", marginBottom: "10px", fontWeight: "bold" }}>
            Warning: This application is not in the expected payment status for draft generation.
          </p>
          <Checkbox
            label="I would like to skip the application payment status validation to generate the PDF"
            checked={skipPaymentValidation}
            onChange={(_, data) => setSkipPaymentValidation(data.checked)}
          />
        </div>
      )}
      <PdfErrorsWarning
        hasPdfErrors={hasPdfErrors}
        pdfErrors={pdfErrors}
        lastUpdatedFillErrors={lastUpdatedFillErrors}
      />
    </Segment>
  );

  const isButtonDisabled = loading || applicant.pdfStatus?.status === 'GENERATING' || pdfGenerationTriggered;

  const pdfErrors = applicant.pdfStatus?.fillErrors || [];
  const hasPdfErrors = pdfErrors.length > 0;
  const lastUpdatedFillErrors = applicant.pdfStatus?.lastUpdatedFillErrors;

  return (
    <div style={{ position: "relative" }}>
      <ConfirmPopup
        content={<CustomContent />}
        callback={() => generatePassportPdf(sendToCustomer)}
        cancelButton="Cancel"
        confirmButton="Generate PDF"
        size="tiny"
        countdown={0}
        onOpen={resetModalState}
      >
        <Button
          style={{
            marginLeft: "auto",
            height: "36px",
            minHeight: "36px"
          }}
          disabled={isButtonDisabled}
        >
          {loading ? (
            <>
              <Loader active inline size="tiny" /> Generating PDF...
            </>
          ) : (
            <>
              {isButtonDisabled && <Loader active inline size="tiny" />}
              <span>Trigger Passport PDF Generation</span>
            </>
          )}
        </Button>
      </ConfirmPopup>
      <PdfErrorsTooltip 
        hasPdfErrors={hasPdfErrors}
        pdfErrors={pdfErrors}
        lastUpdatedFillErrors={lastUpdatedFillErrors}
      />
    </div>
  );
};

export default DownloadPassportPdf;
